import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, money } from "@/lib/api";
import { exportCsv } from "@/lib/exporters";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const METRIC_LABEL = {
  revenue: "Revenue",
  profit: "Profit",
  jobs: "Job count",
  avg_ticket: "Average ticket size",
  margin_pct: "Profit margin %",
};

const GROUPING_LABEL = {
  studio: "Studio",
  brand: "Car brand",
  model: "Car model",
  service: "Service type",
  lead_source: "Lead source",
  installer: "PPF installer",
  cs_person: "Central Sales person",
};

const PERIOD_LABEL = {
  this_month: "This month",
  last_month: "Last month",
  last_3_months: "Last 3 months",
  last_6_months: "Last 6 months",
  custom: "Custom date range",
};

const FILTER_LABEL = {
  none: "No filter",
  studio: "Studio",
  region: "Region",
  service: "Service type",
  installer: "PPF installer",
};

const BLANK = {
  name: "",
  metric: "profit",
  grouping: "studio",
  period: "this_month",
  start_date: "",
  end_date: "",
  filter_type: "none",
  filter_value: "",
};

const ANALYSIS_COLUMNS = [
  { label: "Group", value: (r) => r.group },
  { label: "Jobs", value: (r) => r.jobs },
  { label: "Revenue", value: (r) => r.revenue },
  { label: "Material cost", value: (r) => r.material_cost },
  { label: "Profit", value: (r) => r.profit },
  { label: "Avg ticket", value: (r) => r.avg_ticket },
  { label: "Margin %", value: (r) => r.margin_pct },
];

export const CustomAnalysis = () => {
  const [options, setOptions] = useState(null);
  const [saved, setSaved] = useState([]);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/analytics/saved").then(({ data }) => setSaved(data));

  useEffect(() => {
    api.get("/analytics/options").then(({ data }) => setOptions(data));
    load();
  }, []);

  const runPreview = async (cfg) => {
    try {
      const { data } = await api.post("/analytics/run", cfg);
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const save = async () => {
    try {
      const payload = { ...draft, name: draft.name || "Untitled analysis" };
      if (draft.id) await api.put(`/analytics/saved/${draft.id}`, payload);
      else await api.post("/analytics/saved", payload);
      toast.success("Analysis saved");
      setDraft(null);
      setPreview(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/analytics/saved/${a.id}`);
      toast.success("Analysis deleted");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const filterValues = () => {
    if (!options || !draft) return [];
    if (draft.filter_type === "studio")
      return options.studios.map((s) => ({ value: s.id, label: s.name }));
    if (draft.filter_type === "installer")
      return options.installers.map((s) => ({ value: s.id, label: s.name }));
    if (draft.filter_type === "region")
      return options.regions.map((r) => ({ value: r, label: r }));
    if (draft.filter_type === "service")
      return options.services.map((r) => ({ value: r, label: r }));
    return [];
  };

  return (
    <div className="mt-10" data-testid="custom-analysis-section">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold text-brand-ink">Custom analysis</h2>
          <p className="text-sm text-slate-500">
            Build, save and export your own profit views across studios, brands and installers.
          </p>
        </div>
        <Button
          data-testid="new-analysis-btn"
          onClick={() => {
            setDraft({ ...BLANK });
            setPreview(null);
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> New analysis
        </Button>
      </div>

      {saved.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          No saved analyses yet. Create one to compare studios, brands or installers your way.
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {saved.map((a) => (
          <Card
            key={a.id}
            className="border-brand-line shadow-none"
            data-testid={`analysis-card-${a.id}`}
          >
            <CardHeader className="flex-row flex-wrap items-start justify-between gap-3 space-y-0">
              <div>
                <CardTitle className="text-base">{a.name}</CardTitle>
                <p className="mt-1 text-xs text-slate-500">
                  {METRIC_LABEL[a.config.metric]} by {GROUPING_LABEL[a.config.grouping]} ·{" "}
                  {PERIOD_LABEL[a.config.period]}
                  {a.config.filter_type !== "none" ? ` · filtered` : ""}
                </p>
              </div>
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`analysis-export-${a.id}`}
                  onClick={() =>
                    exportCsv(`veloskin-${a.name || "analysis"}`, ANALYSIS_COLUMNS, a.result.rows)
                  }
                >
                  CSV
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`analysis-edit-${a.id}`}
                  onClick={() => {
                    setDraft({ id: a.id, ...a.config });
                    setPreview(a.result);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`analysis-delete-${a.id}`}
                  onClick={() => remove(a)}
                >
                  <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <AnalysisTable result={a.result} testid={`analysis-table-${a.id}`} />
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Edit analysis" : "New analysis"}</DialogTitle>
          </DialogHeader>
          {draft && options && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>Name</Label>
                <Input
                  className="mt-1.5"
                  data-testid="analysis-name-input"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="Profit by brand — last 3 months"
                />
              </div>
              <Picker
                label="Metric"
                testid="analysis-metric-select"
                value={draft.metric}
                onChange={(v) => setDraft((d) => ({ ...d, metric: v }))}
                items={options.metrics.map((m) => ({ value: m, label: METRIC_LABEL[m] }))}
              />
              <Picker
                label="Grouping"
                testid="analysis-grouping-select"
                value={draft.grouping}
                onChange={(v) => setDraft((d) => ({ ...d, grouping: v }))}
                items={options.groupings.map((m) => ({ value: m, label: GROUPING_LABEL[m] }))}
              />
              <Picker
                label="Time period"
                testid="analysis-period-select"
                value={draft.period}
                onChange={(v) => setDraft((d) => ({ ...d, period: v }))}
                items={options.periods.map((m) => ({ value: m, label: PERIOD_LABEL[m] }))}
              />
              <Picker
                label="Filter"
                testid="analysis-filter-select"
                value={draft.filter_type}
                onChange={(v) => setDraft((d) => ({ ...d, filter_type: v, filter_value: "" }))}
                items={options.filters.map((m) => ({ value: m, label: FILTER_LABEL[m] }))}
              />
              {draft.period === "custom" && (
                <>
                  <div>
                    <Label>From</Label>
                    <Input
                      type="date"
                      className="mt-1.5"
                      data-testid="analysis-start-input"
                      value={draft.start_date || ""}
                      onChange={(e) => setDraft((d) => ({ ...d, start_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>To</Label>
                    <Input
                      type="date"
                      className="mt-1.5"
                      data-testid="analysis-end-input"
                      value={draft.end_date || ""}
                      onChange={(e) => setDraft((d) => ({ ...d, end_date: e.target.value }))}
                    />
                  </div>
                </>
              )}
              {draft.filter_type !== "none" && (
                <Picker
                  label={`${FILTER_LABEL[draft.filter_type]} value`}
                  testid="analysis-filter-value-select"
                  value={draft.filter_value || ""}
                  onChange={(v) => setDraft((d) => ({ ...d, filter_value: v }))}
                  items={filterValues()}
                />
              )}
              <div className="sm:col-span-2">
                <Button
                  variant="outline"
                  data-testid="analysis-preview-btn"
                  onClick={() => runPreview(draft)}
                >
                  Run preview
                </Button>
              </div>
              {preview && (
                <div className="sm:col-span-2 overflow-x-auto rounded-md border border-brand-line">
                  <AnalysisTable result={preview} testid="analysis-preview-table" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="save-analysis-btn" onClick={save}>
              Save analysis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Picker = ({ label, value, onChange, items, testid }) => (
  <div>
    <Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="mt-1.5" data-testid={testid}>
        <SelectValue placeholder="Select" />
      </SelectTrigger>
      <SelectContent>
        {items.map((i) => (
          <SelectItem key={i.value} value={i.value}>
            {i.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const AnalysisTable = ({ result, testid }) => (
  <Table data-testid={testid}>
    <TableHeader>
      <TableRow>
        <TableHead>{GROUPING_LABEL[result.grouping]}</TableHead>
        <TableHead className="text-right">Jobs</TableHead>
        <TableHead className="text-right">Revenue</TableHead>
        <TableHead className="text-right">Profit</TableHead>
        <TableHead className="text-right">Avg ticket</TableHead>
        <TableHead className="text-right">Margin %</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {result.rows.length === 0 && (
        <TableRow>
          <TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">
            No delivered jobs in this period.
          </TableCell>
        </TableRow>
      )}
      {result.rows.map((r) => (
        <TableRow key={r.group} className="hover:bg-brand-tint/60">
          <TableCell className="font-medium">{r.group}</TableCell>
          <TableCell className="text-right">{r.jobs}</TableCell>
          <TableCell className="text-right">{money(r.revenue)}</TableCell>
          <TableCell className="text-right font-semibold text-emerald-600">
            {money(r.profit)}
          </TableCell>
          <TableCell className="text-right">{money(r.avg_ticket)}</TableCell>
          <TableCell className="text-right text-slate-500">{r.margin_pct}%</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);
